
import React from 'react';
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Clock, 
  Calculator, 
  DollarSign, 
  Calendar, 
  TrendingUp, 
  Target,
  Sparkles,
  Award,
  BarChart3
} from "lucide-react";

interface RateCardProps {
  hourlyRate: number;
  totalIncome: number;
  monthlyExpenses: number;
  billableHours: number;
}

const RateCard = ({ 
  hourlyRate, 
  totalIncome, 
  monthlyExpenses, 
  billableHours 
}: RateCardProps) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };
  
  const hourlyRateFormatted = formatCurrency(hourlyRate);
  const dailyRate = hourlyRate * 8;
  const weeklyRate = dailyRate * 5;
  const monthlyRate = totalIncome / 12;

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Hero Rate Display */}
      <div className="relative text-center">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/20 to-secondary/20 rounded-3xl blur-2xl"></div>
        <div className="relative p-8 bg-gradient-to-br from-card-bg to-card-hover rounded-3xl border border-primary/30 backdrop-blur-sm">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Award className="w-6 h-6 text-accent-yellow" />
            <Badge 
              variant="secondary" 
              className="bg-gradient-to-r from-accent-yellow/20 to-accent-orange/20 text-text-primary border-accent-yellow/30 text-sm font-semibold"
            >
              Your True Rate
            </Badge>
            <Sparkles className="w-6 h-6 text-accent-yellow" />
          </div>
          <h1 className="text-5xl sm:text-6xl font-bold gradient-text-cosmic mb-2 animate-scale-in">
            {hourlyRateFormatted}
          </h1>
          <p className="text-xl text-text-secondary font-medium">per hour</p>
          <div className="mt-4">
            <Badge className="bg-gradient-to-r from-primary to-secondary text-white px-4 py-1">
              Ready to charge with confidence
            </Badge>
          </div>
        </div>
      </div>

      {/* Rate Breakdown Grid */}
      <Card className="p-8 bg-gradient-to-br from-card-bg to-card-hover border-0 rounded-2xl shadow-2xl">
        <div className="flex items-center gap-3 mb-6">
          <BarChart3 className="w-6 h-6 text-primary" />
          <h3 className="text-xl font-bold text-text-primary">Rate Breakdown</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/20 to-accent-cyan/20 rounded-xl blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-4 bg-card-bg rounded-xl border border-accent-blue/20 hover:border-accent-blue/40 transition-all duration-300">
              <div className="flex items-center gap-2 mb-2">
                <Clock className="w-5 h-5 text-accent-blue" />
                <p className="text-sm font-semibold text-text-secondary">Daily</p>
              </div>
              <p className="text-2xl font-bold text-text-primary">{formatCurrency(dailyRate)}</p>
              <p className="text-xs text-text-secondary mt-1">8 hours</p>
            </div>
          </div>
          
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-accent-green/20 to-accent-cyan/20 rounded-xl blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-4 bg-card-bg rounded-xl border border-accent-green/20 hover:border-accent-green/40 transition-all duration-300">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-5 h-5 text-accent-green" />
                <p className="text-sm font-semibold text-text-secondary">Weekly</p>
              </div>
              <p className="text-2xl font-bold text-text-primary">{formatCurrency(weeklyRate)}</p>
              <p className="text-xs text-text-secondary mt-1">40 hours</p>
            </div>
          </div>
          
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-accent-orange/20 to-accent-yellow/20 rounded-xl blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-4 bg-card-bg rounded-xl border border-accent-orange/20 hover:border-accent-orange/40 transition-all duration-300">
              <div className="flex items-center gap-2 mb-2">
                <DollarSign className="w-5 h-5 text-accent-orange" />
                <p className="text-sm font-semibold text-text-secondary">Monthly</p>
              </div>
              <p className="text-2xl font-bold text-text-primary">{formatCurrency(monthlyRate)}</p>
              <p className="text-xs text-text-secondary mt-1">~173 hours</p>
            </div>
          </div>
          
          <div className="relative group">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/20 to-secondary/20 rounded-xl blur-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            <div className="relative p-4 bg-card-bg rounded-xl border border-primary/20 hover:border-primary/40 transition-all duration-300">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-5 h-5 text-primary" />
                <p className="text-sm font-semibold text-text-secondary">Annual</p>
              </div>
              <p className="text-2xl font-bold text-text-primary">{formatCurrency(totalIncome)}</p>
              <p className="text-xs text-text-secondary mt-1">Target income</p>
            </div>
          </div>
        </div>
      </Card>

      {/* Calculation Details */}
      <Card className="p-8 border-0 rounded-2xl bg-gradient-to-br from-card-bg to-card-hover">
        <div className="space-y-6">
          <div className="flex items-center gap-3">
            <Calculator className="w-6 h-6 text-accent-cyan" />
            <h3 className="text-xl font-bold text-text-primary">How We Calculated This</h3>
          </div>
          <p className="text-text-secondary leading-relaxed">
            Your true hourly rate is calculated by dividing your annual income by your effective billable hours, 
            accounting for time off, expenses, and a risk buffer to ensure you're charging enough to meet your goals.
          </p>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-accent-pink/10 to-accent-orange/10 rounded-xl"></div>
              <div className="relative p-6 bg-card-bg/80 rounded-xl border border-accent-pink/20 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-3">
                  <Target className="w-5 h-5 text-accent-pink" />
                  <p className="text-sm font-semibold text-text-primary">Monthly Expenses</p>
                </div>
                <p className="text-3xl font-bold text-text-primary">{formatCurrency(monthlyExpenses)}</p>
                <p className="text-xs text-text-secondary mt-2">Your business costs</p>
              </div>
            </div>
            
            <div className="relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-accent-blue/10 to-accent-green/10 rounded-xl"></div>
              <div className="relative p-6 bg-card-bg/80 rounded-xl border border-accent-blue/20 backdrop-blur-sm">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-5 h-5 text-accent-blue" />
                  <p className="text-sm font-semibold text-text-primary">Effective Hours</p>
                </div>
                <p className="text-3xl font-bold text-text-primary">{billableHours.toLocaleString()}</p>
                <p className="text-xs text-text-secondary mt-2">Hours per year</p>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default RateCard;
